/* eslint-disable */
//@ts-nocheck
import { useState, useCallback, useEffect } from "react";
import { theme, Layout, Grid, Spin, ConfigProvider, Tour, App } from "antd";
import SideMenu from "components/layout/SideMenu";
import lightTheme from "themes/light.json";
import { buildAntdConfigProps } from "pages/layout/App";
import Portal from "components/layout/Portal";
import QLayoutContext from "contexts/Layout";
import type { ItemData } from "types/layout";
import { I18nextProvider, initReactI18next } from "react-i18next";
import i18next from "i18next";
import ReactGA from "react-ga4";
import GAMap from "tokens/GAMap.json";
import Global from "types/modules/global";

import QFooter from "components/layout/Footer";
import QHeader from "components/layout/Header";
import QLogo from "components/layout/Logo";
import QerpLanding from "components/layout/QerpLanding";
import type { QerpLandingProps } from "components/layout/QerpLanding";
import { useGetPaidAwareness, useIntro } from "hooks/layout";
import { localeFontFamily } from "utils/locale";
import type { AppSearchItemData } from "types/layout";
import { QueryProvider } from "contexts/QueryProvider";
import GetPaidDrawer from "components/layout/GetPaid/GetPaidDrawer";
import TahseelDrawer from "components/layout/Tahseel/TahseelDrawer";
import { mixPanelConfig } from "../../utils/MispanelConfig";
import { openpanelConfig } from "../../utils/openpanel";

const { Sider, Content } = Layout;
const { useToken } = theme;
const { useBreakpoint } = Grid;
const { components, ...globalTokens } = lightTheme;

interface LayoutProps {
  sideMenuData: ItemData[];
  appSearchItems: AppSearchItemData[];
  isMobileRequest: boolean;
  currentActiveItem: string;
  currentActiveMenu: string;
  comments: {
    viewAllPath: string;
    lastComments: Record<string, any>[];
  };
  tasks: {
    viewAllPath: string;
    lastTasks: Record<string, any>[];
  };
  currentUser: {
    label: string;
    href: string;
  };
  organizationName: string;
  isOnBoard: boolean;
  isPos: boolean;
  tenantID: number;
  hideSidebar?: boolean;
  landing?: QerpLandingProps | null;
  qerpHomePath?: string | null;
  aiToolsEnabled?: boolean;
}

type LayoutPageProps = Pick<LayoutProps, "hideSidebar" | "landing" | "qerpHomePath">;

i18next.use(initReactI18next).init({
  resources: {
    [window.I18n.locale]: {
      translation: window.I18n,
    },
  },
  lng: window.I18n.locale,
});

// The antd pack and page direction come from buildAntdConfigProps() (pages/layout/App), which is
// the one place that owns them; this file only needs the font stack. The second, byte-identical
// locale map that used to sit here drifted from that one and, being a Map, answered `undefined`
// for ur/hi/bn — so the whole app lost its font token for exactly the locales POS-1922 added.
globalTokens.fontFamily = localeFontFamily();

const LayoutPage = ({ hideSidebar = false, landing, qerpHomePath }: LayoutPageProps) => {
  const { token } = useToken();
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedWidth, setCollapsedWidth] = useState(80);
  const [siderWidth, setSiderWidth] = useState<string | number>(300);
  const [mobileStyles, setMobileStyles] = useState({});
  const [logoURL, setLogoURL] = useState("qoyodWLogo");
  const [_broken, setBroken] = useState(false);
  // When sidebar is hidden, derive broken from viewport directly (Sider's onBreakpoint never fires).
  const broken = hideSidebar ? screens.lg === false : _broken;

  const intro = useIntro(window.location.pathname);
  const [isMounting, setIsMounting] = useState(true);
  const getPaidAwareness = useGetPaidAwareness();

  useEffect(() => {
    if (broken) {
      setMobileStyles({ top: 64, height: `calc(100vh - 64px)` });
    } else {
      setMobileStyles({});
    }
  }, [broken]);

  useEffect(() => {
    try {
      ReactGA.initialize(process.env.FRONT_END_GA_ID);
    } catch (e) {
      console.error("Invalid GA");
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setIsMounting(false);
    }, 200);
  }, []);

  useEffect(() => {
    if (!isMounting) {
      document.querySelector(".wrapper").classList.remove("hide");
    }
  }, [isMounting]);

  const triggerCollapse = useCallback(() => {
    setCollapsed((prevState) => !prevState);
    if (!broken && !collapsed) {
      setLogoURL("qLogo");
      document.body.classList.add("sidebar-collapse");
    } else if (!broken && collapsed) {
      setLogoURL("qoyodWLogo");
      document.body.classList.remove("sidebar-collapse");
    }
  }, [collapsed, broken]);

  useEffect(() => {
    mixPanelConfig();
    // Additive: OpenPanel joins Mixpanel rather than replacing it, and no-ops
    // when FRONT_END_OPENPANEL_CLIENT_ID / _API_URL aren't set.
    openpanelConfig();
  }, []);

  return (
    <App>
      <Layout>
        {landing && (
          <Portal
            Component={
              <QerpLanding planName={landing.planName} dashboardPath={landing.dashboardPath} />
            }
            htmlRoot="portal__qerp_landing"
          />
        )}
        <Portal
          Component={
            <Layout
              style={{
                position: "fixed",
                zIndex: 1000,
                width: "100%",
                height: 0,
              }}
            >
              {!hideSidebar && (
                <Sider
                  style={{
                    height: "100vh",
                    maxHeight: "100vh",
                    overflow: "auto",
                    backgroundColor: token.colorPrimary,
                    ...mobileStyles,
                  }}
                  breakpoint="lg"
                  collapsedWidth={collapsedWidth}
                  width={siderWidth}
                  onBreakpoint={(broken) => {
                    setBroken(broken);
                    if (broken) {
                      setCollapsedWidth(0);
                      setCollapsed(true);
                      setSiderWidth("100%");
                      setLogoURL("qoyodLogo");
                    } else {
                      setCollapsedWidth(80);
                      setSiderWidth(300);
                      if (collapsed) {
                        setLogoURL("qLogo");
                        document.body.classList.add("sidebar-collapse");
                      } else {
                        setLogoURL("qoyodWLogo");
                        document.body.classList.remove("sidebar-collapse");
                      }
                    }
                  }}
                  trigger={null}
                  collapsible
                  collapsed={collapsed}
                >
                  {broken ? null : (
                    <QLogo logoURL={logoURL} logoHeight={"50%"} href={qerpHomePath ?? undefined} />
                  )}
                  <SideMenu
                    startIntro={() => {
                      intro.setOpen(true);
                      ReactGA.event({
                        category: GAMap.showMeHow.CATEGORY,
                        action: GAMap.showMeHow[window.location.pathname],
                      });
                    }}
                    collapsed={collapsed}
                    broken={broken}
                  />
                </Sider>
              )}
              <Layout>
                <QHeader
                  broken={broken}
                  logoURL={hideSidebar && broken ? "qoyodLogo" : logoURL}
                  startIntro={() => intro.setOpen(true)}
                  collapsed={collapsed}
                  triggerCollapse={triggerCollapse}
                  hideSidebar={hideSidebar}
                />
              </Layout>
            </Layout>
          }
          htmlRoot="portal__sider_header"
        />
        <Portal
          Component={landing ? null : <QFooter broken={broken} />}
          htmlRoot="portal__footer"
        />
        <Portal
          Component={
            isMounting ? (
              <Layout>
                <Content
                  style={{
                    background: token.colorBgContainer,
                    height: "100vh",
                    width: "100vw",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spin size="large" />
                </Content>
              </Layout>
            ) : null
          }
          htmlRoot="portal__loader"
        />
        <Tour
          open={intro.open}
          onClose={() => intro.setOpen(false)}
          steps={intro.steps}
          scrollIntoViewOptions={{ block: "center" }}
        />
        <Tour
          open={getPaidAwareness.open}
          steps={getPaidAwareness.steps}
          onClose={getPaidAwareness.handleClose}
          closeIcon={false}
          disabledInteraction={true}
        />
        <GetPaidDrawer />
        <TahseelDrawer />
      </Layout>
    </App>
  );
};

export default ({
  sideMenuData,
  appSearchItems,
  currentActiveItem,
  currentActiveMenu,
  isMobileRequest,
  currentUser,
  organizationName,
  comments,
  tasks,
  isOnBoard,
  tenantID,
  showReferral,
  isPos,
  hideSidebar,
  landing,
  qerpHomePath,
  aiToolsEnabled,
}: LayoutProps) => (
  <ConfigProvider {...buildAntdConfigProps()}>
    <QueryProvider>
      <QLayoutContext.Provider
        value={{
          sideMenuData,
          appSearchItems,
          currentActiveItem,
          currentActiveMenu,
          isMobileRequest,
          currentUser,
          organizationName,
          comments,
          tasks,
          isOnBoard,
          tenantID,
          showReferral,
          isPos,
          aiToolsEnabled,
        }}
      >
        <I18nextProvider i18n={i18next}>
          <LayoutPage hideSidebar={hideSidebar} landing={landing} qerpHomePath={qerpHomePath} />
        </I18nextProvider>
      </QLayoutContext.Provider>
    </QueryProvider>
  </ConfigProvider>
);
/* eslint-enable */
